import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Tabs,
  Tag,
  Typography,
  Space,
  Skeleton,
  Empty,
  Dropdown,
} from "antd";
import {
  ArrowLeftOutlined,
  RobotOutlined,
  MoreOutlined,
  CloudUploadOutlined,
  InboxOutlined,
  PlayCircleOutlined,
  BarChartOutlined,
  ReadOutlined,
  AuditOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { Badge, Button, PageHeader } from "../../components/Common";
import { useMutations } from "../../hooks/useApiMutation";
import { useConfirm } from "../../hooks/useConfirm";

import ConfigurationTab from "./components/ConfigurationTab";
import FilesTab from "./components/FilesTab";
import ToolsTab from "./components/ToolsTab";
import KnowledgeTab from "./components/KnowledgeTab";
import ReviewTab from "./components/ReviewTab";
import KeysTab from "./components/KeysTab";
import VersionsTab from "./components/VersionsTab";
import PlaygroundTab from "./components/PlaygroundTab";
import AnalyticsTab from "./components/AnalyticsTab";

const { Text } = Typography;

const STATUS_VARIANT = {
  Draft: "neutral",
  Published: "success",
  Archived: "warning",
};

const PROVIDER_COLOR = {
  OpenAI: "green",
  Claude: "purple",
  Vision: "blue",
};

const AgentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { mutate } = useMutations();
  const { confirmAction } = useConfirm();

  // mutate is recreated on every render by useMutations(). Keeping it in a ref
  // lets useCallback depend only on stable ids, breaking the effect → setState → render loop.
  const mutateRef = useRef(mutate);
  mutateRef.current = mutate;

  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("config");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await mutateRef.current("fetchAgent", {
        method: "get",
        endpoint: `/v1/agents/${id}`,
      });
      setAgent(data?.data || null);
    } catch {
      setAgent(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const onPublish = () => {
    confirmAction({
      key: "publishAgent",
      method: "post",
      endpoint: `/v1/agents/${id}/publish`,
      title: "Publish agent",
      content:
        "Publishing makes the agent available for consumer keys. You can keep editing — every change creates a new version.",
      okText: "Publish",
      onSuccess: load,
    });
  };

  const onArchive = () => {
    confirmAction({
      key: "archiveAgent",
      method: "post",
      endpoint: `/v1/agents/${id}/archive`,
      title: "Archive agent",
      content:
        "Archived agents can no longer be used for new chats. Existing keys keep working until revoked.",
      okText: "Archive",
      okType: "danger",
      onSuccess: load,
    });
  };

  if (loading) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }

  if (!agent) {
    return (
      <Empty
        description="Agent not found"
        style={{ marginTop: 80 }}
      >
        <Button onClick={() => navigate("/agents")}>Back to list</Button>
      </Empty>
    );
  }

  const moreMenu = {
    items: [
      agent.status !== "Archived"
        ? {
            key: "archive",
            icon: <InboxOutlined />,
            label: "Archive",
            onClick: onArchive,
          }
        : null,
    ].filter(Boolean),
  };

  const tabs = [
    {
      key: "config",
      label: "Configuration",
      children: <ConfigurationTab agent={agent} onSaved={load} />,
    },
    {
      key: "knowledge",
      label: (
        <span>
          <ReadOutlined /> Knowledge
        </span>
      ),
      children: <KnowledgeTab agentId={agent.agentId} />,
    },
    {
      key: "files",
      label: `Files (${agent.files?.length || 0})`,
      children: <FilesTab agentId={agent.agentId} onChanged={load} />,
    },
    {
      key: "tools",
      label: `Tools (${agent.tools?.length || 0})`,
      children: <ToolsTab agentId={agent.agentId} onChanged={load} />,
    },
    {
      key: "review",
      label: (
        <span>
          <AuditOutlined /> Review
        </span>
      ),
      children: <ReviewTab agentId={agent.agentId} />,
    },
    {
      key: "playground",
      label: (
        <span>
          <PlayCircleOutlined /> Playground
        </span>
      ),
      children: <PlaygroundTab agent={agent} />,
    },
    {
      key: "keys",
      label: "API Keys",
      children: <KeysTab agent={agent} />,
    },
    {
      key: "versions",
      label: `Versions (${agent.versions?.length || 0})`,
      children: <VersionsTab agent={agent} onRestored={load} />,
    },
    {
      key: "analytics",
      label: (
        <span>
          <BarChartOutlined /> Analytics
        </span>
      ),
      children: <AnalyticsTab agent={agent} />,
    },
  ];

  return (
    <div>
      <PageHeader
        leading={
          <Space size={8}>
            <Button
              variant="ghost"
              icon={<ArrowLeftOutlined />}
              aria-label="Back to agents"
              onClick={() => navigate("/agents")}
            />
            <RobotOutlined
              style={{
                fontSize: 26,
                color: "var(--accent-blue)",
                marginTop: 6,
              }}
              aria-hidden="true"
            />
          </Space>
        }
        title={agent.name}
        subtitle={agent.description}
        meta={
          <Space wrap size={[8, 6]}>
            <Tag color={PROVIDER_COLOR[agent.provider]}>{agent.provider}</Tag>
            {agent.model && <Text code>{agent.model}</Text>}
            <Badge variant={STATUS_VARIANT[agent.status] || "neutral"}>
              {agent.status}
            </Badge>
            <Text type="secondary">v{agent.currentVersion}</Text>
            {agent.isGlobal && <Tag color="geekblue">Global</Tag>}
            {agent.companyName && !agent.isGlobal && (
              <Text type="secondary">{agent.companyName}</Text>
            )}
          </Space>
        }
        extra={
          <Space wrap>
            {agent.status !== "Published" && (
              <Button
                variant="primary"
                icon={<CloudUploadOutlined />}
                onClick={onPublish}
              >
                Publish
              </Button>
            )}
            {moreMenu.items.length > 0 && (
              <Dropdown menu={moreMenu} trigger={["click"]}>
                <Button
                  variant="ghost"
                  icon={<MoreOutlined />}
                  aria-label="More agent actions"
                />
              </Dropdown>
            )}
          </Space>
        }
      />

      <Tabs
        size="small"
        tabBarGutter={20}
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabs}
        destroyOnHidden
      />
    </div>
  );
};

export default AgentDetail;
